/**
 * 系统更新公告相关 TanStack Query Hooks
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  getLatestSystemUpdate,
  getSystemUpdateDetail,
  getSystemUpdateList,
} from '@/services/systemUpdateService'
import type {
  SystemUpdate,
  SystemUpdateListData,
  SystemUpdateListParams,
} from '@/types/systemUpdate'

/** 系统更新公告列表查询 Key */
const SYSTEM_UPDATES_QUERY_KEY = ['system-updates'] as const

/** 系统更新公告详情查询 Key */
const SYSTEM_UPDATE_DETAIL_QUERY_KEY = ['system-update', 'detail'] as const

/** 最新系统更新公告查询 Key */
const LATEST_SYSTEM_UPDATE_QUERY_KEY = ['system-update', 'latest'] as const

/**
 * 系统更新公告列表查询结果
 */
export interface SystemUpdateListResult {
  list: SystemUpdate[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

/**
 * 获取系统更新公告列表
 * @param params 查询参数
 * @returns 查询结果
 */
export function useSystemUpdates(
  params?: SystemUpdateListParams
): UseQueryResult<SystemUpdateListResult> {
  return useQuery({
    queryKey: [...SYSTEM_UPDATES_QUERY_KEY, params] as const,
    queryFn: async (): Promise<SystemUpdateListResult> => {
      const data: SystemUpdateListData = await getSystemUpdateList(params)
      const pageSize = data.page_size !== 0 ? data.page_size : (params?.pageSize ?? 10)
      const totalPages = pageSize > 0 ? Math.ceil(data.total / pageSize) : 1
      return {
        list: data.list,
        total: data.total,
        page: data.page,
        pageSize,
        totalPages,
      }
    },
  })
}

/**
 * 获取系统更新公告详情
 * @param id 公告 ID
 * @returns 查询结果
 */
export function useSystemUpdateDetail(
  id: string | undefined
): UseQueryResult<SystemUpdate> {
  return useQuery({
    queryKey: [...SYSTEM_UPDATE_DETAIL_QUERY_KEY, id] as const,
    queryFn: async (): Promise<SystemUpdate> => {
      if (id === undefined || id === '') {
        throw new Error('公告 ID 不能为空')
      }
      return await getSystemUpdateDetail(id)
    },
    enabled: id !== undefined && id !== '',
  })
}

/**
 * 获取最新系统更新公告
 * @returns 查询结果
 */
export function useLatestSystemUpdate(): UseQueryResult<SystemUpdate | null> {
  return useQuery({
    queryKey: [...LATEST_SYSTEM_UPDATE_QUERY_KEY] as const,
    queryFn: async (): Promise<SystemUpdate | null> => {
      return await getLatestSystemUpdate()
    },
    staleTime: 5 * 60 * 1000,
  })
}

export {
  SYSTEM_UPDATES_QUERY_KEY,
  SYSTEM_UPDATE_DETAIL_QUERY_KEY,
  LATEST_SYSTEM_UPDATE_QUERY_KEY,
}
